import { Input } from '@components/Input';
import { MenuSelect, type MenuSelectItem } from '@components/MenuSelect';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './TypeAhead.module.scss';

export interface TypeAheadProps {
  /** Array of selectable items */
  items: MenuSelectItem[];
  /** Currently selected value */
  value?: string | null;
  /** Callback when selection changes */
  onChange?: (value: string | null) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Optional leading icon for the input */
  leadingIcon?: React.ReactNode;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Show clearable button */
  clearable?: boolean;
  /** Maximum height of dropdown before scrolling (default: 240px) */
  maxHeight?: number;
  /** Filter function - defaults to case-insensitive label match */
  filterFn?: (item: MenuSelectItem, query: string) => boolean;
  /** Callback when input value changes (for controlled filtering) */
  onInputChange?: (value: string) => void;
  /** Additional CSS classes */
  className?: string;
}

const defaultFilterFn = (item: MenuSelectItem, query: string): boolean => {
  const lowerQuery = query.toLowerCase();
  return (
    item.label.toLowerCase().includes(lowerQuery) ||
    (item.subtitle?.toLowerCase().includes(lowerQuery) ?? false)
  );
};

export const TypeAhead = ({
  items,
  value,
  onChange,
  placeholder = '',
  leadingIcon,
  disabled = false,
  clearable = false,
  maxHeight = 240,
  filterFn = defaultFilterFn,
  onInputChange,
  className = '',
}: TypeAheadProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get the label for the selected value
  const selectedItem = items.find((item) => item.value === value);

  // Filter items based on input
  const filteredItems = inputValue ? items.filter((item) => filterFn(item, inputValue)) : items;

  // Initialize input value from selected item
  const [hasInitialized, setHasInitialized] = useState(false);
  if (!hasInitialized && selectedItem) {
    setInputValue(selectedItem.label);
    setHasInitialized(true);
  }

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Reset input to selected value's label when closing
        if (selectedItem) {
          setInputValue(selectedItem.label);
        } else {
          setInputValue('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedItem]);

  const handleInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);
      setIsOpen(true);
      onInputChange?.(newValue);
    },
    [onInputChange]
  );

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleSelect = (itemValue: string) => {
    const selected = items.find((item) => item.value === itemValue);
    if (selected) {
      setInputValue(selected.label);
      onChange?.(itemValue);
    }
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      if (selectedItem) {
        setInputValue(selectedItem.label);
      }
    } else if (e.key === 'Enter' && filteredItems.length > 0) {
      // Select first filtered item on Enter
      const firstItem = filteredItems[0];
      if (firstItem && !firstItem.disabled) {
        handleSelect(firstItem.value);
      }
    }
  };

  const handleClear = () => {
    setInputValue('');
    onChange?.(null);
    setIsOpen(false);
  };

  const containerClassName = [styles.container, className].filter(Boolean).join(' ');

  return (
    <div ref={containerRef} className={containerClassName}>
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        leadingIcon={leadingIcon}
        disabled={disabled}
        clearable={clearable}
        onClear={handleClear}
        className={styles.input}
      />
      {isOpen && (
        <div className={styles.dropdown}>
          {filteredItems.length > 0 ? (
            <MenuSelect
              items={filteredItems}
              value={value}
              onChange={handleSelect}
              maxHeight={maxHeight}
            />
          ) : (
            <div className={styles.noResults}>No results found</div>
          )}
        </div>
      )}
    </div>
  );
};
